import { effect, inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TUI_DARK_MODE } from '@taiga-ui/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import { TokenModel } from '@src/api';
import { TelegramAuthService } from '@src/app/modules/telegram';
import { logger } from '@src/utils';
import { AppConfigService } from '@src/app/modules/app-config';
import { InternalMessengerService } from '@src/app/modules/internal-messenger';
import { CitySwitcherService } from '@src/app/modules/search-cities/city-switcher';
import { OnlineStatusService } from '@src/core/services';
import { UserUI } from '@src/models';
import { WelcomeDialogsService } from '@src/app/shared/dialogs';

import { CallbackAfterCreateSession, JwtTokenPayload } from '../types';

import { AuthService } from './auth.service';
import { AuthUserService } from './auth-user.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly authUserService = inject(AuthUserService);
  private readonly telegramAuth = inject(TelegramAuthService);
  private readonly translateService = inject(TranslateService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly internalMessengerService = inject(InternalMessengerService);
  readonly darkMode = inject(TUI_DARK_MODE);

  private resolve?: (value: unknown) => void;

  private callbacksAfterCreateSession: CallbackAfterCreateSession[] = [];

  private readonly onlineStatusService = inject(OnlineStatusService);
  private readonly citySwitcherService = inject(CitySwitcherService);

  private readonly welcomeDialogsService = inject(WelcomeDialogsService);
  readonly welcomeDialogsCompleted$ = this.welcomeDialogsService.dialogsCompleted$;

  readonly authUser$ = this.authUserService.authUser$;

  constructor() {
    this.appConfigService.data$.subscribe(appConfig => {
      if (appConfig) {
        if (appConfig?.botId && appConfig?.botName) {
          this.telegramAuth.setMainBot(appConfig.botId, appConfig.botName);
        }

        this.restoreSession().then(this.resolve);
      }
    });

    this.telegramAuth.success$.subscribe(() => {
      logger('SessionService logged in Telegram');
    });

    effect(() => {
      if (this.welcomeDialogsService.callChangeToken()) {
        if (this.currentParentOrganisationId) {
          this.changeToken(this.currentParentOrganisationId).subscribe();
        }
      }
    });
  }

  get appConfig() {
    return this.appConfigService.data$.value;
  }

  get authUser() {
    return this.authUserService.user;
  }

  get currentParentOrganisationId() {
    return this.authUserService.user?.parentOrganisationId;
  }

  get currentCity() {
    return this.citySwitcherService.currentCity$.value;
  }

  addCallbackAfterCreateSession(callback: CallbackAfterCreateSession) {
    this.callbacksAfterCreateSession.push(callback);
  }

  isLogged(): boolean {
    return this.authService.isLogged();
  }

  isLoggedTelegram(): boolean {
    return !!this.telegramAuth.isLogged();
  }

  init() {
    return new Promise(resolve => {
      this.resolve = resolve;
      this.appConfigService.loadData();
    });
  }

  /**
   * Восстанавливаем сессию
   */
  async restoreSession() {
    const token = this.authService.readTokenFromStorage();
    const refreshToken = this.authService.readRefreshTokenFromStorage();

    if (token && refreshToken) {
      await this.createSession({ token, refreshToken });
    }

    if (this.telegramAuth.useTelegram) {
      await this.telegramAuth.authenticate();
    }
  }

  /**
   * Создаем сессию
   * @param sessionData токены
   * @param redirect флаг, что нужно произвести редирект на returnUrl
   */
  async createSession({ token, refreshToken }: TokenModel, redirect?: boolean) {
    if (!token) {
      throw new Error(this.translateService.instant('components.auth.alerts.errors.noToken'));
    }

    this.authService.setTokens({ token, refreshToken });

    const jwtHelper = new JwtHelperService();
    const decodedToken = jwtHelper.decodeToken<JwtTokenPayload>(token);

    if (!decodedToken) {
      throw new Error(this.translateService.instant('components.auth.alerts.errors.noDecodedToken'));
    }

    // Закрываем возможность получения токена с помощью бота
    this.telegramAuth.preventGetTokenFromBot();
    const user = await this.authUserService.init(decodedToken);

    await Promise.all(this.callbacksAfterCreateSession.map(callback => callback(user)));

    const returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
    if (redirect) {
      await this.router.navigateByUrl(returnUrl ?? '');
    }

    this.onlineStatusService.isOnline$.subscribe(async res => {
      if (res) {
        if (this.appConfig?.internalMessengerUrl && token && this.authUser?.id) {
          await this.internalMessengerService.init(this.appConfig.internalMessengerUrl, token, this.authUser.id);
        }
      } else {
        await this.internalMessengerService.logOut();
      }
    });

    if (this.appConfig && this.authUser) {
      this.welcomeDialogsService.init(this.appConfig, this.authUser);
    }

    return user;
  }

  changeToken(parentOrganisationId: string) {
    return this.authService.changeToken(parentOrganisationId);
  }

  updateAuthUser(user: Partial<UserUI>) {
    this.authUserService.updateAuthUser(user);
  }
}
